import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { marketplaceService } from '../../../services/marketplaceService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const UserListings = () => {
  const [activeTab, setActiveTab] = useState('marketplace');
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserListings();
  }, [user]);

  const fetchUserListings = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await marketplaceService?.getUserListings();
      
      // Transform data to match UI expectations
      const transformedData = data?.map(item => ({
        id: item?.id,
        type: 'marketplace',
        title: item?.title,
        description: item?.description,
        price: `${item?.price?.toLocaleString('tr-TR')} ₺`,
        status: item?.is_sold ? 'sold' : 'active',
        image: item?.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image',
        imageAlt: `${item?.title} görsel`,
        views: 0, // Not tracked in current schema
        created_at: item?.created_at
      }));
      
      setMarketplaceListings(transformedData || []);
    } catch (err) {
      console.error('Error fetching user listings:', err);
      setError(err?.message || 'İlanlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Bu ilanı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await marketplaceService?.deleteItem(listingId);
      // Refresh listings
      await fetchUserListings();
    } catch (err) {
      console.error('Error deleting listing:', err);
      alert('İlan silinirken bir hata oluştu: ' + err?.message);
    }
  };

  const handleMarkAsSold = async (listingId) => {
    try {
      await marketplaceService?.markAsSold(listingId);
      // Refresh listings
      await fetchUserListings();
    } catch (err) {
      console.error('Error marking as sold:', err);
      alert('İlan güncellenirken bir hata oluştu: ' + err?.message);
    }
  };

  const carpoolListings = []; // Carpool not implemented yet

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-success bg-success/10',
      pending: 'text-warning bg-warning/10',
      sold: 'text-muted-foreground bg-muted',
      completed: 'text-muted-foreground bg-muted'
    };
    return colors?.[status] || 'text-muted-foreground bg-muted';
  };

  const getStatusText = (status) => {
    const texts = {
      active: 'Aktif',
      pending: 'Beklemede',
      sold: 'Satıldı',
      completed: 'Tamamlandı'
    };
    return texts?.[status] || status;
  };

  const currentListings = activeTab === 'marketplace' ? marketplaceListings : carpoolListings;

  return (
    <div className="bg-card rounded-xl shadow-organic p-4 md:p-6 lg:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 flex items-center gap-2">
        <Icon name="List" size={24} />
        İlanlarım
      </h2>
      <div className="flex gap-2 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('marketplace')}
          className={`px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-smooth whitespace-nowrap ${
            activeTab === 'marketplace' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon name="ShoppingBag" size={16} />
            <span>Pazar Yeri ({marketplaceListings?.length})</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('carpool')}
          className={`px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-smooth whitespace-nowrap ${
            activeTab === 'carpool' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon name="Car" size={16} />
            <span>Araç Paylaşımı ({carpoolListings?.length})</span>
          </div>
        </button>
      </div>
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">İlanlar yükleniyor...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-red-600" />
            </div>
            <p className="text-red-600 mb-4">{error}</p>
            <Button variant="outline" size="default" onClick={fetchUserListings}>
              Tekrar Dene
            </Button>
          </div>
        ) : currentListings?.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon
                name={activeTab === 'marketplace' ? 'ShoppingBag' : 'Car'}
                size={32}
                className="text-muted-foreground"
              />
            </div>
            <p className="text-muted-foreground mb-4">Henüz ilan bulunmuyor</p>
            <Button 
              variant="outline" 
              size="default" 
              iconName="Plus" 
              iconPosition="left"
              onClick={() => navigate('/user-marketplace?action=create')}
            >
              Yeni İlan Ekle
            </Button>
          </div>
        ) : (
          currentListings?.map((listing) => (
            <div
              key={listing?.id}
              className="bg-background rounded-lg border border-border p-4 hover:shadow-organic transition-smooth"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                {listing?.image && (
                  <div className="w-full sm:w-32 h-32 rounded-lg overflow-hidden flex-shrink-0">
                    <Image
                      src={listing?.image}
                      alt={listing?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h3 className="text-base md:text-lg font-semibold text-foreground mb-1 line-clamp-1">
                        {listing?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {listing?.description}
                      </p>
                    </div>
                    <div
                      className={`${getStatusColor(listing?.status)} px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap`}
                    >
                      {getStatusText(listing?.status)}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 mb-3 text-xs md:text-sm text-muted-foreground">
                    {listing?.type === 'marketplace' ? (
                      <>
                        <div className="flex items-center gap-1">
                          <Icon name="DollarSign" size={14} />
                          <span className="font-semibold text-primary">{listing?.price}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={14} />
                          <span>{new Date(listing?.created_at)?.toLocaleDateString('tr-TR')}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-1">
                          <Icon name="MapPin" size={14} />
                          <span className="line-clamp-1">{listing?.route}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={14} />
                          <span>{listing?.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Users" size={14} />
                          <span>
                            {listing?.seatsAvailable}/{listing?.totalSeats} koltuk
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      iconName="Eye"
                      onClick={() => navigate(`/marketplace/${listing?.id}`)}
                    >
                      Görüntüle
                    </Button>
                    {listing?.status !== 'sold' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Edit"
                        onClick={() => navigate(`/user-marketplace?edit=${listing?.id}`)}
                      >
                        Düzenle
                      </Button>
                    )}
                    {listing?.status === 'active' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        iconName="CheckCircle"
                        onClick={() => handleMarkAsSold(listing?.id)}
                      >
                        Satıldı Olarak İşaretle
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="Trash2"
                      onClick={() => handleDelete(listing?.id)}
                    >
                      Sil
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UserListings;